import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { forwardRef, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    MAT_MOMENT_DATE_ADAPTER_OPTIONS,
    MAT_MOMENT_DATE_FORMATS,
    MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { AumicoCoreModule, AumicoPatternModule } from '@modeso/aumico-lib-core-fe';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { I18NextModule, I18NextService } from 'angular-i18next';
import { QuillModule } from 'ngx-quill';
import { AumicoComponentsModule } from '../../../aumico-components/src/lib';

import { AumicoAnnualReportConfig, AumicoAnnualReportConfigService } from './aumico-annualreport.config';
import { AumicoAiAgentsModule } from '@modeso/aumico-lib-ai-agents-fe';
import { AccountProofGermanDisplayComponent } from './components/account-proof/account-proof-german-display/account-proof-german-display.component';
import { AccountProofSwissDisplayComponent } from './components/account-proof/account-proof-swiss-display/account-proof-swiss-display.component';
import { AppropriationOfProfitGermanDisplayComponent } from './components/appropriation-of-profit-display/appropriation-of-profit-german-display/appropriation-german-display.component';
import { AppropriationOfProfitSwissDisplayComponent } from './components/appropriation-of-profit-display/appropriation-of-profit-swiss-display/appropriation-swiss-display.component';
import { ArTemplateTaxonomyComponent } from './components/ar-templates/ar-template-taxonomy/ar-template-taxonomy.component';
import { ArTemplatesListComponent } from './components/ar-templates/ar-templates-list/ar-templates-list.component';
import { CashFlowFinancingActivitiesComponent } from './components/cash-flow-statement-components/cash-flow-from-financing-activities/cash-flow-financing-activities.component';
import { CashFlowInvestingActivitiesComponent } from './components/cash-flow-statement-components/cash-flow-from-investing-activities/cash-flow-investing-activities.component';
import { CashFlowOperatingActivitiesComponent } from './components/cash-flow-statement-components/cash-flow-from-operating-activities/cash-flow-operating-activities.component';
import { CashFlowStatementGermanDisplayComponent } from './components/cash-flow-statement-components/cash-flow-statement-display/cash-flow-statement-german-display/cash-flow-german-display.component';
import { CashFlowStatementSwissDisplayComponent } from './components/cash-flow-statement-components/cash-flow-statement-display/cash-flow-statement-swiss-display/cash-flow-statement-swiss-display.component';
import { CashFlowTotalComponent } from './components/cash-flow-statement-components/cash-flow-total/cash-flow-total.component';
import { CashFlowTotalFromActivitiesComponent } from './components/cash-flow-statement-components/cash-flow-total-from-activities/cash-flow-total-from-activities.component';
import { CashflowInfoComponent } from './components/cash-flow-statement-components/cashflow-info/cashflow-info.component';
import { DeleteCashFlowFliComponent } from './components/cash-flow-statement-components/delete-cash-flow-fli/delete-cash-flow-fli.component';
import { OverridableValueInputComponent } from './components/cash-flow-statement-components/overridable-value-input/overridable-value-input.component';
import { ComplementaryReportGermanDisplayComponent } from './components/complementary-report-display/complementary-report-german-display/complementary-report-german-display.component';
import { ComplementaryReportSwissDisplayComponent } from './components/complementary-report-display/complementary-report-swiss-display/complementary-report-swiss-display.component';
import { ComplementaryReportsComponent } from './components/complementary-reports/complementary-reports.component';
import { EditAnnualReportTemplatesTranslationsComponent } from './components/edit-annual-report-templates-translations/edit-annual-report-templates-translations.component';
import { EqualityCheckPrintPreviewTable } from './components/equality-check-print-preview-table/equality-check-print-preview-table.component';
import { EquityMovementScheduleGermanDisplayComponent } from './components/equity-movement-schedule-display/equity-movement-schedule-german-display/equity-movement-schedule-german-display.component';
import { EquityMovementScheduleSwissDisplayComponent } from './components/equity-movement-schedule-display/equity-movement-schedule-swiss-display/equity-movement-schedule-swiss-display.component';
import { EquityMovementScheduleComponent } from './components/equity-movement-schedule/equity-movement-schedule.component';
import { FinancialStatementImportDefinitionComponent } from './components/financial-statement-import-definition/financial-statement-import-definition.component';
import { FinancialStatementImportInterpretationComponent } from './components/financial-statement-import-interpretation/financial-statement-import-interpretation.component';
import { FinancialStatementImportOptionsComponent } from './components/financial-statement-import-options/financial-statement-import-options.component';
import { TemplateSpecialFlisFormComponent } from './components/financial-statement-special-fli-settings/special-fli-settings.component';
import { FixedAssetsScheduleGermanDisplayComponent } from './components/fixed-assets-schedule-display/fixed-assets-schedule-german-display/fixed-assets-schedule-german-display.component';
import { FixedAssetsScheduleSwissDisplayComponent } from './components/fixed-assets-schedule-display/fixed-assets-schedule-swiss-display/fixed-assets-schedule-swiss-display.component';
import { FixedAssetsScheduleForm } from './components/fixed-assets-schedule-form/fixed-assets-schedule-form.component';
import { FixedAssetsScheduleTable } from './components/fixed-assets-schedule-table/fixed-assets-schedule-table.component';
import { MismatchDisclaimerComponent } from './components/mismatch-disclaimer/mismatch-disclaimer.component';
import { PrintOptionsComponent } from './components/print-options/print-options.component';
import { CcrComponent } from './components/ccr/ccr.component';
import { ProfitCalculationComponent } from './components/profit-calculation/profit-calculation.component';
import { ProjectSelectionListComponent } from './components/project-selection-list/project-selection-list.component';
import { ProposalComponent } from './components/proposal/proposal.component';
import { RedoAnnualReportComponent } from './components/redo-annual-report/redo-annual-report.component';
import { SealHistoryComponent } from './components/seal-history/seal-history.component';
import { SpreadSheetPreviewComponent } from './components/spreadsheet-preview/spreadsheet-preview.component';
import { TaxonomyPreviewComponent } from './components/taxonomy-preview/taxonomy-preview.component';
import { TextSnippetComponent } from './components/text-snippet/text-snippet.component';
import { TextSnippetsListDialogComponent } from './components/text-snippets-list-dialog/text-snippets-list-dialog.component';
import { TextSnippetsListComponent } from './components/text-snippets-list/text-snippets-list.component';
import {
    ANNUAL_REPORT_AR_TEMPLATES_FEATURE_KEY,
    ANNUAL_REPORT_FEATURE_KEY,
    ANNUAL_REPORT_TEXT_SNIPPET_FEATURE_KEY,
} from './constants/feature-state-key';
import { BalanceSheetCurrentDateChangeListenerDirective } from './directives/balance-sheet-from-date-change-listener.directive';
import { AnnaulReportTemplatesEffects } from './effects/annual-report-templates.effects';
import { AnnualReportSharesEffects } from './effects/annualReport-shares.effects';
import { AnnualReportEffects } from './effects/annualReport.effects';
import { TextSnippetEffects } from './effects/text-snippet.effects';
import { WorkflowDocumentsEffects } from './effects/workflow-documents.effects';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { GeneralInformationBaseComponent } from './components/general-information-v2/base/general-information-base.component';
import { GeneralInformationV2Component } from './components/general-information-v2/general-information-v2-wrapper.component';
import { GeneralInformationAutofillComponent } from './components/general-information-v2/modes/general-information-autofill.component';
import { GeneralInformationNewComponent } from './components/general-information-v2/modes/general-information-new.component';
import { GeneralInformationSavedComponent } from './components/general-information-v2/modes/general-information-saved.component';
import { ComplementaryReportFilesEffects } from './effects/complementary-report-files.effects';
import { DatevExcelHelper } from './helpers/datev.excel.helper';
import { DatevAiAdapter } from './helpers/datev-ai.adapter';
import { DatevTaxonomyPreviewDialogComponent } from './components/datev-taxonomy-preview-dialog/datev-taxonomy-preview-dialog.component';
import { AnnualReportListPage } from './pages/AnnualReportList/annual-report-list.page';
import { AppropriationOfProfitGermanPrintPreviewPage } from './pages/appropriation-of-profit-print-preview/appropriation-of-profit-german-print-preview/appropriation-german-print-preview.page';
import { AppropriationOfProfitSwissPrintPreviewPage } from './pages/appropriation-of-profit-print-preview/appropriation-of-profit-swiss-print-preview/appropriation-swiss-print-preview.page';
import { AppropriationOfProfitPage } from './pages/appropriation-of-profit/appropriation-of-profit.page';
import { ArTemplateNotesPageComponent } from './pages/ar-templates/ar-template-notes/ar-template-notes.page';
import { ArTemplateSettingsPageComponent } from './pages/ar-templates/ar-template-settings/ar-template-settings.page';
import { ArTemplateTaxonomyPageComponent } from './pages/ar-templates/ar-template-taxonomy/ar-template-taxonomy.page';
import { ArTemplateGeneralInformationFormPageComponent } from './pages/ar-templates/ar-templates-general-information-form/ar-template-general-information-form.page';
import { ArTemplatesPdfPageComponent } from './pages/ar-templates/ar-templates-pdf/ar-templates-pdf.page';
import { CashFlowStatementPage } from './pages/cash-flow-statement/cash-flow-statement.page';
import { ComplementaryReportTitleComponent } from './pages/complementary-reports/complementary-report-title/complementary-report-title.component';
import { ComplementaryReportsPage } from './pages/complementary-reports/complementary-reports.page';
import { EquityMovementSchedulePage } from './pages/equity-movement-schedule/equity-movement-schedule.page';
import { FinancialStatementImportFormPage } from './pages/financial-statement-import-form/financial-statement-import-form.page';
import { InReportStepsPipe } from './pipes/in-report-steps.pipe';
import { navigationFeature } from './reducer/annual-report-navigation.reducer';
import { annualReportSharesFeature } from './reducer/annual-report-shares.reducer';
import * as fromAnnualReportTemplatesReducer from './reducer/annual-report-templates.reducer';
import * as fromReducer from './reducer/annualReport.reducer';
import * as fromComplementaryReportFilesReducer from './reducer/complementary-report-files.reducer';
import { COMPLEMENTARY_REPORT_FILES_FEATURE_KEY } from './reducer/complementary-report-files.reducer';
import * as fromTextSnippetReducer from './reducer/text-snippet.reducer';
import * as fromWorkflowDocumentsReducer from './reducer/workflow-documents.reducer';
import { DOCUMENTS_FEATURE_KEY } from './reducer/workflow-documents.reducer';
import { AumicoAnnualReportTemplatesService } from './services/annual-report-template.service';
import { AumicoAnnualReportService } from './services/annualReport.service';
import { AumicoTextSnippetService } from './services/text-snippet.service';
export { ArTemplateNotesPageComponent } from './pages/ar-templates/ar-template-notes/ar-template-notes.page';
export function matDateLocaleFactory(i18nextService: I18NextService): string {
    return i18nextService.language;
}

const EXPORTED_DECLARATIONS = [
    CcrComponent,
    GeneralInformationV2Component,
    GeneralInformationBaseComponent,
    GeneralInformationNewComponent,
    GeneralInformationAutofillComponent,
    GeneralInformationSavedComponent,
    AnnualReportListPage,
    PrintOptionsComponent,
    FinancialStatementImportDefinitionComponent,
    FinancialStatementImportInterpretationComponent,
    FinancialStatementImportOptionsComponent,
    SpreadSheetPreviewComponent,
    FinancialStatementImportFormPage,
    ProfitCalculationComponent,
    RedoAnnualReportComponent,
    ProposalComponent,
    AppropriationOfProfitPage,
    CashFlowOperatingActivitiesComponent,
    CashFlowInvestingActivitiesComponent,
    CashFlowFinancingActivitiesComponent,
    CashFlowTotalComponent,
    CashFlowTotalFromActivitiesComponent,
    CashFlowStatementPage,
    CashFlowStatementGermanDisplayComponent,
    CashFlowStatementSwissDisplayComponent,
    AppropriationOfProfitGermanPrintPreviewPage,
    AppropriationOfProfitSwissPrintPreviewPage,
    AppropriationOfProfitGermanDisplayComponent,
    AppropriationOfProfitSwissDisplayComponent,
    AccountProofGermanDisplayComponent,
    AccountProofSwissDisplayComponent,
    ProjectSelectionListComponent,
    EqualityCheckPrintPreviewTable,
    ComplementaryReportGermanDisplayComponent,
    ComplementaryReportSwissDisplayComponent,
    EquityMovementScheduleComponent,
    EquityMovementSchedulePage,
    EquityMovementScheduleGermanDisplayComponent,
    EquityMovementScheduleSwissDisplayComponent,
    ComplementaryReportsComponent,
    ComplementaryReportsPage,
    TextSnippetComponent,
    TextSnippetsListComponent,
    TextSnippetsListDialogComponent,
    ArTemplatesListComponent,
    ArTemplateGeneralInformationFormPageComponent,
    ArTemplateTaxonomyComponent,
    ArTemplateTaxonomyPageComponent,
    ArTemplatesPdfPageComponent,
    ArTemplateNotesPageComponent,
    ArTemplateSettingsPageComponent,
    FixedAssetsScheduleTable,
    FixedAssetsScheduleForm,
    FixedAssetsScheduleGermanDisplayComponent,
    FixedAssetsScheduleSwissDisplayComponent,
    TaxonomyPreviewComponent,
    TemplateSpecialFlisFormComponent,
    EditAnnualReportTemplatesTranslationsComponent,
    CashflowInfoComponent,
    MismatchDisclaimerComponent,
    SealHistoryComponent,
    ComplementaryReportTitleComponent,
    DatevTaxonomyPreviewDialogComponent,
];

const ANGULAR_MATERIAL = [
    MatButtonToggleModule,
    MatExpansionModule,
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatTabsModule,
    MatFormFieldModule,
    MatDividerModule,
    MatSelectModule,
    MatDatepickerModule,
];

@NgModule({
    declarations: [
        EXPORTED_DECLARATIONS,
        BalanceSheetCurrentDateChangeListenerDirective,
        DeleteCashFlowFliComponent,
        OverridableValueInputComponent,
        InReportStepsPipe,
    ],
    providers: [
        DatevExcelHelper,
        DatevAiAdapter,
        { provide: MAT_DATE_LOCALE, useFactory: matDateLocaleFactory, deps: [I18NextService] },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ],
    imports: [
        CommonModule,
        AumicoComponentsModule,
        I18NextModule,
        AumicoCoreModule,
        FormsModule,
        ReactiveFormsModule,
        QuillModule.forRoot({}),
        DragDropModule,
        ScrollingModule,
        AumicoPatternModule,
        StoreModule.forFeature(navigationFeature),
        StoreModule.forFeature(ANNUAL_REPORT_FEATURE_KEY, fromReducer.reducer),
        StoreModule.forFeature(ANNUAL_REPORT_TEXT_SNIPPET_FEATURE_KEY, fromTextSnippetReducer.TextSnippetReducer),
        StoreModule.forFeature(DOCUMENTS_FEATURE_KEY, fromWorkflowDocumentsReducer.documentsReducer),
        StoreModule.forFeature(
            COMPLEMENTARY_REPORT_FILES_FEATURE_KEY,
            fromComplementaryReportFilesReducer.complementaryReportFilesReducer,
        ),
        StoreModule.forFeature(
            ANNUAL_REPORT_AR_TEMPLATES_FEATURE_KEY,
            fromAnnualReportTemplatesReducer.annualReportTemplatesReducer,
        ),
        StoreModule.forFeature(annualReportSharesFeature),
        EffectsModule.forFeature([
            AnnualReportEffects,
            TextSnippetEffects,
            AnnaulReportTemplatesEffects,
            AnnualReportSharesEffects,
            WorkflowDocumentsEffects,
            ComplementaryReportFilesEffects,
        ]),
        HttpClientModule,
        MatDialogModule,
        forwardRef(() => AumicoComponentsModule),
        AumicoAiAgentsModule,
        ANGULAR_MATERIAL,
    ],
    exports: [EXPORTED_DECLARATIONS],
})
export class AumicoAnnualReportModule {
    static forRoot(config: AumicoAnnualReportConfig): ModuleWithProviders<AumicoAnnualReportModule> {
        return {
            ngModule: AumicoAnnualReportModule,
            providers: [
                AumicoAnnualReportService,
                AumicoTextSnippetService,
                AumicoAnnualReportTemplatesService,
                {
                    provide: AumicoAnnualReportConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
